/**
 * When to notify about tasks. Pure: given tasks, settings and "now", which
 * reminder occurrences exist. Each occurrence has a stable key, so delivery can
 * be idempotent (never twice) and snoozes/dismissals can be remembered.
 */
import type { TaskPriority, TaskStatus } from './model';

export interface NotificationSettings {
  enabled: boolean;
  /** Show OS notifications (browser Notification API / desktop). In-app is always on. */
  system: boolean;
  quietHours: { enabled: boolean; start: string; end: string }; // "22:00" → "07:00"
  /** Rule: a reminder on the due date at this time (or at the due time when it has one). */
  dueRule: { enabled: boolean; time: string };
  /** Rule: high/highest priority also one day before the due date. */
  highPriorityDayBefore: { enabled: boolean; time: string };
  dailySummary: { enabled: boolean; time: string };
  weeklySummary: { enabled: boolean; weekday: number; time: string }; // 0 = Sunday
  defaultSnoozeMinutes: number;
  mutedProjects: string[];
  mutedTags: string[];
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  enabled: true,
  system: true,
  quietHours: { enabled: true, start: '22:00', end: '07:00' },
  dueRule: { enabled: true, time: '09:00' },
  highPriorityDayBefore: { enabled: true, time: '16:00' },
  dailySummary: { enabled: true, time: '08:30' },
  weeklySummary: { enabled: false, weekday: 5, time: '16:00' },
  defaultSnoozeMinutes: 10,
  mutedProjects: [],
  mutedTags: [],
};

export interface ReminderTask {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority | null;
  due: string | null; // YYYY-MM-DD
  /** Time of the due date ("14:00"); without it the due rule's time is used. */
  dueTime?: string | null;
  reminders: string[]; // YYYY-MM-DD or YYYY-MM-DDTHH:mm
  relativeReminders: number[]; // minutes before due
  projectId: string | null;
  tags: string[];
}

export type OccurrenceKind = 'explicit' | 'relative' | 'due' | 'dayBefore';

export interface Occurrence {
  /** Stable identity: task + moment (+ kind). */
  key: string;
  taskId: string;
  kind: OccurrenceKind;
  fireAt: number; // epoch ms, local time
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Local date + "HH:mm" → epoch ms. */
export function localTime(date: string, time: string): number {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y!, m! - 1, d!, hh ?? 0, mm ?? 0).getTime();
}

export function localDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const isOpenStatus = (s: TaskStatus) => s !== 'done' && s !== 'cancelled';

function muted(task: ReminderTask, s: NotificationSettings): boolean {
  if (task.projectId && s.mutedProjects.includes(task.projectId)) return true;
  return task.tags.some((t) => s.mutedTags.some((m) => t === m || t.startsWith(`${m}/`)));
}

/** All reminder occurrences of open, unmuted tasks. */
export function occurrences(tasks: ReminderTask[], s: NotificationSettings): Occurrence[] {
  if (!s.enabled) return [];
  const out: Occurrence[] = [];
  for (const task of tasks) {
    if (!isOpenStatus(task.status) || muted(task, s)) continue;
    const add = (kind: OccurrenceKind, fireAt: number) =>
      out.push({ key: `${task.id}|${kind}|${fireAt}`, taskId: task.id, kind, fireAt });
    for (const r of task.reminders) {
      add('explicit', r.length > 10 ? localTime(r, r.slice(11, 16)) : localTime(r, s.dueRule.time));
    }
    if (task.due) {
      const dueAt = localTime(task.due, task.dueTime ?? s.dueRule.time);
      for (const minutes of task.relativeReminders) add('relative', dueAt - minutes * 60_000);
      // The default rule only applies when the task has no reminder of its own.
      const own = task.reminders.length + task.relativeReminders.length > 0;
      if (s.dueRule.enabled && !own) add('due', dueAt);
      if (
        s.highPriorityDayBefore.enabled &&
        (task.priority === 'high' || task.priority === 'highest')
      ) {
        const dayBefore = new Date(localTime(task.due, s.highPriorityDayBefore.time));
        dayBefore.setDate(dayBefore.getDate() - 1);
        add('dayBefore', dayBefore.getTime());
      }
    }
  }
  return out.sort((a, b) => a.fireAt - b.fireAt);
}

/** Is `ms` inside the quiet hours (which may span midnight)? */
export function inQuietHours(ms: number, s: NotificationSettings): boolean {
  if (!s.quietHours.enabled) return false;
  const d = new Date(ms);
  const minutes = d.getHours() * 60 + d.getMinutes();
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const start = toMin(s.quietHours.start);
  const end = toMin(s.quietHours.end);
  return start <= end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}

export type SnoozeChoice = { minutes: number } | 'tomorrow' | { at: number };

/** When a snoozed reminder comes back. "Tomorrow" means tomorrow at the due-rule time. */
export function snoozeUntil(choice: SnoozeChoice, now: number, s: NotificationSettings): number {
  if (choice === 'tomorrow') {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    return localTime(localDate(d.getTime()), s.dueRule.time);
  }
  if ('at' in choice) return choice.at;
  return now + choice.minutes * 60_000;
}

/** Summary moments (daily / weekly) up to `now` for today. */
export function summaryOccurrences(
  now: number,
  s: NotificationSettings,
): { key: string; kind: 'daily' | 'weekly'; fireAt: number }[] {
  if (!s.enabled) return [];
  const today = localDate(now);
  const out: { key: string; kind: 'daily' | 'weekly'; fireAt: number }[] = [];
  if (s.dailySummary.enabled) {
    const at = localTime(today, s.dailySummary.time);
    out.push({ key: `summary|daily|${today}`, kind: 'daily', fireAt: at });
  }
  if (s.weeklySummary.enabled && new Date(now).getDay() === s.weeklySummary.weekday) {
    const at = localTime(today, s.weeklySummary.time);
    out.push({ key: `summary|weekly|${today}`, kind: 'weekly', fireAt: at });
  }
  return out;
}
